// Theme Context
// ==============
// Manages dark mode state and persistence

import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  setDarkMode: (value: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { user, updateUser } = useAuth();
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Initialize dark mode from user preferences or system preference
  useEffect(() => {
    if (user?.preferences?.darkMode !== undefined) {
      setIsDarkMode(user.preferences.darkMode);
    } else {
      // Fall back to system preference
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(systemPrefersDark);
    }
  }, [user?.preferences?.darkMode]);

  // Apply dark class to document
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleDarkMode = async () => {
    const newValue = !isDarkMode;
    setIsDarkMode(newValue);

    // Persist to user preferences if logged in
    if (user) {
      await updateUser({
        preferences: { ...user.preferences, darkMode: newValue }
      });
    }
  };

  const setDarkMode = async (value: boolean) => {
    setIsDarkMode(value);

    // Persist to user preferences if logged in
    if (user) {
      await updateUser({
        preferences: { ...user.preferences, darkMode: value }
      });
    }
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleDarkMode, setDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
};
